import { ref, computed } from "vue";
import useAPIRequest from "@/composables/useAPIRequest";
import { MediaDTO, MediaModel } from "@/entities/Media";

interface MediaQueryParams {
  page: number;
  limit: number;
  dateFrom?: string;
  dateTo?: string;
  order: string;
  type?: string;
  excludeType?: string;
}

const fetchMediasQuery = ({
  page,
  limit,
  dateFrom,
  dateTo,
  order,
  type,
  excludeType,
}: MediaQueryParams) => {
  const from = limit * page - limit;
  const to = limit * page;
  const baseQuery = `
*[
  _type == 'media'
  ${dateFrom ? `&& premierDate >= "${dateFrom}"` : ``}
  ${dateTo ? `&& premierDate <= "${dateTo}"` : ``}
  ${type ? `&& type == "${type}"` : ``}
  ${excludeType ? `&& type != "${excludeType}"` : ``}
  && !(_id in path('drafts.**'))
  && defined(title)
  && defined(slug)
  && defined(type)
  && defined(premierDate)
  && defined(poster)
  && defined(body)
] | order(premierDate ${order})
{
  _id,
  title,
  "slug": slug.current,
  type,
  isFeatured,
  premierDate,
  "poster": poster.asset->url,
  mediaId
}`;

  return `{
      'results': ${baseQuery}[${from}...${to}],
      'count': count(${baseQuery}),
      'isLastPage': count(${baseQuery}[${from}...${to}]) < ${limit}
  }`;
};

export default function useMedia() {
  const medias = ref<MediaModel[]>([]);
  const totalResultsCount = ref(0);
  const isLastPage = ref(false);

  const { fetch: _fetchMedias, requestState: requestStateMedias } =
    useAPIRequest<{
      results: MediaDTO[];
      count: number;
      isLastPage: boolean;
    }>();

  const fetchMedias = async ({
    page,
    limit,
    dateFrom,
    dateTo,
    order,
    type,
    excludeType,
  }: MediaQueryParams) => {
    await _fetchMedias(
      fetchMediasQuery({
        page,
        limit,
        dateFrom,
        dateTo,
        order,
        type,
        excludeType,
      })
    );
    if (
      requestStateMedias.value.status === "ready" &&
      requestStateMedias.value.data
    ) {
      totalResultsCount.value = requestStateMedias.value.data.count;
      isLastPage.value = requestStateMedias.value.data.isLastPage;
      medias.value.push(
        ...requestStateMedias.value.data.results.map(
          (m: MediaDTO) => new MediaModel(m)
        )
      );
    }
  };

  const featuredMedias = computed(() => {
    return medias.value.filter((m) => m.isFeatured);
  });

  function resetMedias() {
    medias.value = [];
  }

  return {
    fetchMedias,
    medias,
    featuredMedias,
    requestStateMedias,
    resetMedias,
    totalResultsCount,
    isLastPage,
  };
}
